import { api } from '@shared/api';
import { mapLookupResult, type BackendLookupResult, type LookupResult } from './workspaceApi';

/** Размер новой подборки (2026-10-09): 10 или 20 слов. В истории бывают и старые 5/15. */
export type TopicCount = 10 | 20;

/** 1 AI-генерация за каждые 10 слов подборки — как на бэке (topic_generation_cost). */
export const topicBatchCost = (count: number) => Math.max(1, Math.ceil(count / 10));

export interface TopicItem {
  id: string;
  word: string;
  recommended: { pos: string | null; translation: string };
  recommendedSenses: { pos: string | null; translation: string }[];
  lookup: LookupResult;
}

export interface TopicBatch {
  id: string;
  topic: string;
  description: string | null;
  requestedCount: number;
  status: 'generating' | 'ready' | 'failed';
  createdAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  items: TopicItem[];
  itemCount: number;
}

export interface TopicAvailability {
  items: {
    itemId: string;
    word: string;
    senses: {
      pos: string | null;
      translation: string;
      available: boolean;
      learned: boolean;
      generating: boolean;
      ready: boolean;
    }[];
  }[];
}

interface BackendBatch {
  id: string;
  topic: string;
  description: string | null;
  requested_count: number;
  status: TopicBatch['status'];
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  item_count?: number;
  items?: (Omit<TopicItem, 'lookup' | 'recommendedSenses'> & {
    recommended_senses?: { pos: string | null; translation: string }[];
    lookup: BackendLookupResult;
  })[];
}

const mapBatch = (batch: BackendBatch): TopicBatch => ({
  id: batch.id,
  topic: batch.topic,
  description: batch.description,
  requestedCount: batch.requested_count,
  status: batch.status,
  createdAt: batch.created_at,
  completedAt: batch.completed_at,
  errorMessage: batch.error_message,
  itemCount: batch.item_count ?? batch.items?.length ?? 0,
  items: (batch.items ?? []).map((item) => ({
    id: item.id,
    word: item.word,
    recommended: item.recommended,
    recommendedSenses: item.recommended_senses?.length ? item.recommended_senses : [item.recommended],
    lookup: mapLookupResult(item.lookup, item.word),
  })),
});

export async function fetchTopicBatches(offset = 0, signal?: AbortSignal) {
  const { data } = await api.get<{ items: BackendBatch[]; total: number }>('/topic-batches', {
    params: { limit: 20, offset },
    signal,
  });
  return { items: data.items.map(mapBatch), total: data.total };
}

export async function fetchTopicBatch(id: string, signal?: AbortSignal) {
  const { data } = await api.get<BackendBatch>(`/topic-batches/${encodeURIComponent(id)}`, { signal });
  return mapBatch(data);
}

export async function fetchTopicAvailability(
  id: string,
  moduleId: string,
  signal?: AbortSignal,
): Promise<TopicAvailability> {
  const { data } = await api.get<{
    items: (Omit<TopicAvailability['items'][number], 'itemId'> & { item_id: string })[];
  }>(`/topic-batches/${encodeURIComponent(id)}/availability`, {
    params: { module_id: moduleId },
    signal,
    // На холодном кэше сервер сверяет каждое слово со словарём по очереди: 20 слов дольше
    // общего таймаута 15 с (замер 2026-10-12), со вторым запросом — быстро.
    timeout: 60_000,
  });
  return { items: data.items.map((item) => ({ itemId: item.item_id, word: item.word, senses: item.senses })) };
}

export async function generateTopicBatch(args: {
  topic: string;
  description: string;
  count: TopicCount;
  requestId: string;
}) {
  const { data } = await api.post<BackendBatch>('/topic-batches', {
    topic: args.topic.trim(),
    description: args.description.trim() || null,
    count: args.count,
    request_id: args.requestId,
  });
  return mapBatch(data);
}

export const topicKeys = {
  history: (userId: string) => ['topic-batches', userId, 'history'] as const,
  batch: (userId: string, id: string) => ['topic-batches', userId, 'batch', id] as const,
  lookup: (userId: string, moduleId: string | null, word: string) => ['topic-lookup', userId, moduleId, word] as const,
  availability: (userId: string, batchId: string, moduleId: string | null) =>
    ['topic-availability', userId, batchId, moduleId] as const,
};
